import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * Label/value tile (design-spec §3 "Info tile"): sand fill, radius 14,
 * padding 14×16; uppercase 11.5/700 label over a 15/700 ink value.
 * Used for WHEN / SERVICE / WHERE on the confirmation and account screens.
 *
 * Renders a `<div>` holding a `<dt>`/`<dd>` pair when `asDescription` is set
 * (wrap several tiles in a `<dl>`), otherwise plain paragraphs.
 */
export interface InfoTileProps {
  label: ReactNode;
  value: ReactNode;
  icon?: ReactNode;
  asDescription?: boolean;
  className?: string;
}

export function InfoTile({
  label,
  value,
  icon,
  asDescription = false,
  className,
}: InfoTileProps) {
  const LabelTag = asDescription ? 'dt' : 'p';
  const ValueTag = asDescription ? 'dd' : 'p';
  return (
    <div className={cn('rounded-14 bg-sl-sand px-4 py-3.5', className)}>
      <LabelTag className="font-sans text-[11.5px] font-bold uppercase tracking-[1.2px] text-sl-label-2">
        {label}
      </LabelTag>
      <ValueTag className="mt-1 flex items-center gap-2 font-sans text-[15px] font-bold text-sl-ink">
        {icon}
        {value}
      </ValueTag>
    </div>
  );
}
